import type { Product, ProductAttributesOptionsColor } from '#root/api/clients/product/data-contracts'
import type { AttributeSelection, OptionGroups, ProductDetail, ProductExtended, UseFetchOptionsExt } from '#types/product'

export async function useProduct(
  id: MaybeRefOrGetter<string>,
  options: UseFetchOptionsExt
) {
  const { $feature, $t } = useNuxtApp()
  const { products } = useApi()
  const { benefitRatingsMaxScale, disableGroupBy } = useAppConfig().pages?.pdp || {}
  const route = useRoute()
  const productId = toValue(id)
  const detailsId = computed(() => options?.enableColorAsAttribute ? `${toValue(id)}${route.query?.color || ''}` : toValue(id))
  const isStyle = computed(() => PRODUCT_STYLE_ID_REGEX.test(detailsId.value))

  const { pending: inventoryPending, data: inventory, error: inventoryError } = products.inventory(id, {}, {
    lazy: true,
    server: false,
    key: `productInventory-${productId}`
  })

  const { pending: productPending, data: product, error: productError }
    = products.details<ProductExtended>(detailsId, {}, {
      lazy: true,
      key: `productDetails-${detailsId.value}`,
      ...options
    })

  return {
    inventoryPending,
    inventoryError,
    inventory,
    productPending,
    productError,
    product: computed(() => {
      if (!product.value) return null

      const sanitizedDetails = (product.value.details || []).map((detail) => ({
        ...detail,
        data: detail.data.map((content) => {
          for (const key in content) {
            if (typeof content[key] === 'string' && content[key].match(HTML_REGEX)) {
              if ($feature.allowDetailsLineBreaksOnPdp) content[key] = content[key].replace(LINE_BREAK_REGEX, '\n')
              content[key] = content[key].replace(HTML_REGEX, '')
            }
          }

          return content
        })
      }))

      const sanitizedModelMeasurements = product.value.modelMeasurements?.map((modelMeasurement) =>
        modelMeasurement.match(HTML_REGEX) ? modelMeasurement.replace(HTML_REGEX, '') : modelMeasurement
      )

      const isBenefitRating = ({ id }) => id === 'benefitRatings'
      const groupedDetails = sanitizedDetails.filter((item) => !isBenefitRating(item))
      const benefitRatingIndex = sanitizedDetails.findIndex(isBenefitRating)

      if (benefitRatingIndex !== -1) {
        const groupedBenefitRatings = {
          expanded: sanitizedDetails[benefitRatingIndex].expanded,
          id: 'benefitRatings',
          layout: 'scale',
          label: $t.benefitRating,
          data: sanitizedDetails
            .filter(isBenefitRating)
            .flatMap(({ label, data }) => data.map(
              ({ maxValue, value, minLabel }) => ({
                maxValue: benefitRatingsMaxScale!.toString(),
                value: maxValue,
                minLabel: value,
                maxLabel: minLabel,
                label
              })
            ))
        } satisfies ProductDetail

        groupedDetails.splice(benefitRatingIndex, 0, groupedBenefitRatings)
      }

      const defaultColor = getDefaultColorOption(product.value.attributes, product.value.id) || {}
      const isDigital = product.value.productType?.toLowerCase() === 'digital'
      const isRobisOnly = $feature.allowProductReservationOnPDP && product.value?.isRobisOnly
      const isPerpetual = isDigital || isCustomsProduct(product) || isRobisOnly

      let variants = Array.isArray(product.value.variants) ? product.value?.variants : []

      if (options?.enableColorAsAttribute && isStyle.value && defaultColor.value)
        variants = variants.filter((variant) => variant.attributes.color === defaultColor.value)

      variants = variants.map((variant) => {
        const quantity = isPerpetual ? 1 : inventory.value?.variants[variant.id]?.quantity || 0
        const stockStatus = isPerpetual ? 'AVAILABLE' : inventory.value?.variants[variant.id]?.stockStatus || 'OUT OF STOCK'

        return {
          ...variant,
          productInventoryState: quantity > 0 ? 'InStock' : 'OutOfStock' as 'InStock' | 'OutOfStock',
          stockStatusLabel: $t.stockStatus[stockStatus]
        }
      })

      // Check if there is any attribute option that needs to be marked as unavailable according to its inventory
      const attributes = product.value.attributes?.map((attribute) => {
        // TEMP: mitigation against API not returning type field in attribute
        if ('code' in attribute) {
          // @ts-expect-error incorrect contract type
          attribute.type = attribute.code
          delete attribute.code
        }

        const getColorAvailability = (option): boolean => {
          if (option.id === product.value?.id)
            return variants.some(({ productInventoryState }) => productInventoryState === 'InStock')

          if (options?.enableColorAsAttribute && inventory.value?.variants) {
            const colorInventory = Object.entries(inventory.value.variants)
              .filter(([key, _]) => key.includes(`:${option.value}:`))

            return colorInventory.some(([_, { quantity }]) => quantity > 0)
          }
          return option.available
        }
        const attributeOptions = attribute.options.filter((option) => !(isDigital && option.label === 'X')).map((option) => {
          // VariantIds are a list of all variant IDs
          const variantIds = variants
            .filter((variant) => variant.attributes[attribute.type] === option.value)
            .map((variant) => variant.id)
          return attribute.type === 'color'
            ? {
                ...option,
                available: getColorAvailability(option)
              }
            : {
                ...option,
                label: option.label,
                available: variants.filter(({ id }) => variantIds?.includes(id))
                  .some(({ productInventoryState }) => productInventoryState === 'InStock')
              }
        })

        return ({
          ...attribute,
          isSingleton: attributeOptions.length === 1,
          options: attributeOptions
        })
      })

        .map((attribute) => {
          const isSamePrice = (price1, price2) => Object.keys(price1).filter((key) => typeof price1[key] === 'number'
            && typeof price2[key] === 'number').every((key) => price1[key] === price2[key])

          const groupOptions = (attribute) => {
            const groups: OptionGroups[] = []
            if (!disableGroupBy && attribute.groupBy === 'price') {
              const uniquePrices = [attribute.options[0].price].filter(Boolean)

              // find unique prices
              attribute.options.forEach((option) => {
                if (option.price && uniquePrices.every((price) => !isSamePrice(price, option.price)))
                  uniquePrices.push(option.price)
              })

              // sort prices from highest to lowest
              uniquePrices.sort((a, b) => a.lowCurrent < b.lowCurrent ? 1 : -1)

              // create an option group for each unique price
              uniquePrices.forEach((price) => {
                groups.push({
                  groupValue: price,
                  options: attribute.options.filter((option) => isSamePrice(price, option.price))
                })
              })
            }
            else {
              groups.push({ options: attribute.options })
            }
            return groups
          }

          return ({
            ...attribute,
            groupedOptions: groupOptions(attribute)
          })
        })

      return {
        ...product.value,
        allVariantsOutOfStock: variants.every(({ productInventoryState }) => productInventoryState === 'OutOfStock'),
        attributes,
        badge: formatBadgeOrEyebrow(defaultColor.badge
          || product.value.badge, product.value.targetPublishDate, $feature.configProductComingSoonDateFormat),
        defaultColor: defaultColor.value,
        customsExternalUrl: product.value.customsExternalUrl?.replace(/\/customizer\.(.*?)\.html/g, '/customizer/$1'),
        details: groupedDetails,
        eyebrow: formatBadgeOrEyebrow(defaultColor.eyebrow
          || product.value.eyebrow, product.value.targetPublishDate, $feature.configProductComingSoonDateFormat),
        id: defaultColor.id || product.value.id,
        modelMeasurements: sanitizedModelMeasurements,
        price: { ...product.value.price, ...defaultColor.price },
        variants,
      }
    })
  }
}

export function useProductColor(product: MaybeRefOrGetter<Product | null>) {
  return computed(() =>
    toValue(product)?.attributes.reduce((colorObject: ProductAttributesOptionsColor | null, attribute) =>
      attribute.type === 'color' && !colorObject
        ? attribute.options.find(({ id }) => id === toValue(product)?.id) as ProductAttributesOptionsColor
        : colorObject, null) || null)
}

export function useAttributeSelectionLabels(
  product: MaybeRefOrGetter<Product | null>,
  selection: MaybeRefOrGetter<AttributeSelection>
) {
  const productColor = useProductColor(product)
  return computed(() => (toValue(product)?.attributes || []).reduce((attributes, { type, options, label }) => ({
    ...attributes,
    [type]: {
      label,
      value: type === 'color' ? productColor.value?.label : options?.find(({ value }) => value === toValue(selection)[type])?.label
    }
  }), {} as Record<string, { label: string, value: string }>))
}
